import type {
    TDealPropertyInfo,
    TDealView,
    TExtractionInfo,
    TPropertySummary,
    TUserDatabase,
} from "@/apis/odin/apiODIN";
import { isNullOrUndefined } from "@/utils/guards";

type DealExtractionInfo =
    | (TDealPropertyInfo & {
          isParent: boolean;
          numOfExtractions?: number;
          databaseCompanyName: string;
          numFiles: number;
          properties: TPropertySummary[];
      })
    | ((TExtractionInfo | { projectId: number; addFile: boolean }) & {
          parentDealName: string;
          databaseId: number;
          databaseCompanyName: string;
          allowUserDeletion: boolean;
      });

export const transformDealView = ({
    deals,
    userDatabases,
    searchPhrase,
}: {
    deals: TDealView[];
    userDatabases: TUserDatabase[];
    searchPhrase: string;
}): DealExtractionInfo[] => {
    const lowerSearchPhrase = searchPhrase.toLowerCase();

    const matchesDealSearchCriteria = (deal: TDealView): boolean => {
        const dealInfo = deal.dealInfo;
        if (
            dealInfo.dealName.toLowerCase().includes(lowerSearchPhrase) ||
            dealInfo.dealId.toString().includes(lowerSearchPhrase)
        ) {
            return true;
        }
        return (dealInfo.properties ?? []).some(
            property =>
                property.propertyName
                    .toLowerCase()
                    .includes(lowerSearchPhrase) ||
                property.fullAddress.toLowerCase().includes(lowerSearchPhrase),
        );
    };

    const transformedRowData = deals.filter(matchesDealSearchCriteria).reduce(
        (acc, deal) => {
            let tempAcc = acc.rows;
            let addFileAllowed = acc.addFileAllowed;
            const databaseCompany = userDatabases.find(
                db => db.databaseId === deal.dealInfo.databaseId,
            );

            const nonDeletedExtractions = deal.extractions.filter(extraction =>
                isNullOrUndefined(extraction.deletedAt),
            );

            const parentRow = {
                ...deal.dealInfo,
                properties: deal.dealInfo.properties ?? [],
                isParent: true,
                numOfExtractions: nonDeletedExtractions.length,
                databaseCompanyName: `${databaseCompany?.databaseCompanyName} | ${databaseCompany?.databaseName}`,
                numFiles: deal.numFiles,
            };
            const childRows = nonDeletedExtractions.map(extraction => ({
                ...extraction,
                parentDealName: deal.dealInfo.dealName,
                sheetName: `${extraction.sheetName} Table ${extraction.extractionId}`,
                databaseId: deal.dealInfo.databaseId,
                databaseCompanyName: `${databaseCompany?.databaseCompanyName} | ${databaseCompany?.databaseName}`,
                allowUserDeletion: deal.dealInfo.allowUserDeletion,
            }));

            tempAcc = [...tempAcc, parentRow, ...childRows];

            if (nonDeletedExtractions.length === 0 && addFileAllowed) {
                tempAcc = [
                    ...tempAcc,
                    {
                        addFile: true,
                        projectId: deal.dealInfo.projectId,
                        parentDealName: deal.dealInfo.dealName,
                        databaseId: deal.dealInfo.databaseId,
                    } as DealExtractionInfo,
                ];
                addFileAllowed = false;
            }

            return {
                rows: tempAcc,
                addFileAllowed: addFileAllowed,
            };
        },
        {
            rows: [] as DealExtractionInfo[],
            addFileAllowed: true,
        },
    );

    return transformedRowData.rows;
};
